import { useExamContext } from "@features/exam/contexts/ExamContext";
import { useSessionItemsNavLayout } from "@features/session/contexts/SessionItemsNavLayoutContext";
import {
	getItemAudioUri,
	hasListeningItems,
} from "@features/session/helpers/content";
import {
	isIntroScreen,
	isMicCheckIntroItem,
} from "@features/session/helpers/intro-screens";
import {
	getAllowPrevNavigation,
	getNextGatedUntilAudioAnswered,
	getNextGatedUntilResponseComplete,
	getSummativeResponseConfig,
} from "@features/session/helpers/response-timing";
import useCurrentSession from "@features/session/hooks/useCurrentSession";
import useResponseControlsStore from "@features/session/hooks/useResponseControlsStore";
import useSessionStore from "@features/session/hooks/useSessionStore";
import { useAppStore } from "@hooks/useAppStore";
import { useGlobalStore } from "@hooks/useGlobalStore";
import { hasStaticFeedback } from "@memorang/helpers/feedback";
import { resolveMaxScore } from "@memorang/helpers/scoring/max-score";
import { isAiSubmittable } from "@memorang/helpers/session";
import { useDevice } from "@memorang/ui/useDevice";
import { useLocalSearchParams } from "expo-router";
import { useTranslation } from "react-i18next";
import { studyResourcesLink } from "../../constants";
import { helpDialog } from "../../constants";
import { constructExamResourcesMarkdown } from "../../constants/dialog-configs";
import useAIFeedbackStore from "../../hooks/useAIFeedbackStore";
import { getShowSubmitAnswerAIFeedbackButton } from "./helpers";
import {
	getNextButtonStates,
	getShouldShowConfidencesSubmitButtons,
	getShowButtonNavFinish,
} from "./next-button-state";

const useSessionBottomBarState = () => {
	const { index } = useLocalSearchParams<{ index: string }>();
	const currentItemIndex = index ? Number.parseInt(index) - 1 : 0;

	const id = useSessionStore((s) => s.id);
	const inReviewMode = useSessionStore((s) => s.inReviewMode);
	const isSummativeTest = useSessionStore((s) => s.isSummativeTest);
	const currentBlockIndex = useSessionStore((s) => s.currentBlockIndex);
	const timeElapsed = useSessionStore((s) => s.timeElapsed);
	const sessionTime = useSessionStore((s) => s.sessionTime);
	const numGoal = useSessionStore((s) => s.numGoal);
	const numericId = useSessionStore((s) => s.numericId);
	const totalTime = useSessionStore((s) => s.totalTime);
	const sessionSubType = useSessionStore((s) => s.sessionSubType);
	const sessionItems = useSessionStore((s) => s.sessionItems);
	const blockStates = useSessionStore((s) => s.blockStates);
	const itemStates = useSessionStore((s) => s.itemStates);
	const collectionId = useSessionStore((s) => s.collectionId);
	const showBreakDialog = useSessionStore((s) => s.showBreakDialog);
	const serverExamBreak = useSessionStore((s) => s.serverExamBreak);
	const breaks = useSessionStore((s) => s.breaks);
	const isBreakOngoing = useSessionStore((s) => s.isBreakOngoing);
	const completedResponseItems = useResponseControlsStore(
		(s) => s.completedItems,
	);
	const recordingActiveItems = useResponseControlsStore(
		(s) => s.recordingActiveItems,
	);

	const updateProgress = useSessionStore((s) => s.updateProgress);
	const updateCurrentBlockIndex = useSessionStore(
		(s) => s.updateCurrentBlockIndex,
	);
	const toggleShowBreakDialog = useSessionStore((s) => s.toggleShowBreakDialog);

	const {
		currentItem,
		currentBlock,
		numSessionItems,
		numTotalItems,
		parts,
		currentPart,
		currentPartIndex,
		numTotalParts,
		sectionIndexWithinPart,
	} = useCurrentSession();

	const currentItemId = currentItem?.id!;
	const response = currentItem.response;
	const maximumScore = resolveMaxScore(currentItem.scoring);
	const isAiSubmittableResponse = isAiSubmittable(
		response?.type,
		currentItem.metadata?.type,
		currentItem.metadata?.toeic_artifact_type,
		hasStaticFeedback(currentItem.feedback),
	);

	const sectionId = currentItem.metadata?.section;
	const currentItemState = itemStates?.get(currentItemId);

	const currentBlockState = blockStates?.get(currentBlockIndex);
	const markedItems = currentBlockState?.markedItemsForReview;
	const highlightedItems = currentBlockState?.highlightedItems;
	const isMarked =
		currentItem?.answer?.marked || markedItems?.includes(currentItemId);
	const highlightedHtml =
		currentItem?.answer?.highlight || highlightedItems?.get(currentItemId);

	const slideshowDurationInSeconds = useAIFeedbackStore(
		(s) => s.slideshowDurationInSecondsByItemId[currentItemId],
	);
	const userResponse = useAIFeedbackStore((s) => s.userResponse);
	const audioResponse = useAIFeedbackStore((s) => s.audioResponse);
	const streaming = useAIFeedbackStore((s) => s.streaming);

	const diagnosticLabel = useAppStore((s) => s.exam.diagnosticLabel);
	const helpDialogConfig = useAppStore((s) => s.sessionView?.helpDialog);

	const hasShownConfidenceOnboardingDialog = useGlobalStore(
		(s) => s.hasShownConfidenceOnboardingDialog,
	);
	const setHasShownConfidenceOnboardingDialog = useGlobalStore(
		(s) => s.setHasShownConfidenceOnboardingDialog,
	);

	const {
		currentExamContext: { examId, examName, projectId, organizationId },
	} = useExamContext();

	const { isMobile, isLargeWindowSize } = useDevice();
	const sessionItemsNav = useSessionItemsNavLayout();
	const { t } = useTranslation();

	const lastBreak = breaks?.length ? breaks[breaks.length - 1] : null;
	const isBreakExpired = lastBreak?.status === "EXPIRED";

	const isLastBlock = isSummativeTest
		? currentBlockIndex === sessionItems.length - 1
		: false;

	const isLastItemInTheBlock = currentItemIndex === currentBlock.length - 1;
	const sessionLength = currentBlock.length;
	const isLastItem = currentItemIndex === sessionLength - 1;

	const numSections = numSessionItems;

	const fullState = useSessionStore((s) => s);

	const showConfidenceSubmitButtons = getShouldShowConfidencesSubmitButtons(
		fullState,
		currentItemIndex,
	);
	const {
		text: nextButtonText,
		disabled: disableNextButton,
		showButtonNavSkip,
		tooltip: nextButtonTooltip,
	} = getNextButtonStates(
		fullState,
		currentItemIndex,
		t,
		userResponse?.[currentItemId],
	);

	const isResponseComplete = Boolean(completedResponseItems[currentItemId]);
	const isRecordingActive = Boolean(recordingActiveItems[currentItemId]);
	const recordingActiveTooltip = isRecordingActive
		? t("session.navigation.recordingInProgressTooltip")
		: undefined;
	const gateNextUntilResponseComplete =
		getNextGatedUntilResponseComplete(response, isSummativeTest) &&
		!isResponseComplete &&
		!isRecordingActive;

	const responseConfig = getSummativeResponseConfig(
		response,
		isSummativeTest && !inReviewMode,
	);
	const gateNextUntilAudioAnswered = getNextGatedUntilAudioAnswered({
		config: responseConfig,
		hasAudio: Boolean(getItemAudioUri(currentItem?.content)),
		hasSelectedAnswer: !showButtonNavSkip,
	});

	const isMicCheckGated =
		currentItem !== undefined &&
		isMicCheckIntroItem(currentItem) &&
		!userResponse?.[currentItemId];

	const showButtonNavFinish = getShowButtonNavFinish(
		fullState,
		currentItemIndex,
	);
	const { show: showSubmitAnswerAIFeedbackButton } =
		getShowSubmitAnswerAIFeedbackButton({
			sessionState: fullState,
			currentItemIndex,
			currentUserResponse: userResponse?.[currentItemId],
			currentAudioResponse: audioResponse?.[currentItemId],
			shouldValidateAudio: false,
		});

	const studyResourcesMarkdown = studyResourcesLink[examId]
		? constructExamResourcesMarkdown(studyResourcesLink[examId].links)
		: undefined;

	const confirmDialogConfig = helpDialog({
		sessionItems: currentBlock,
		numSections,
		examName,
		totalTime,
		isDiagnosticTest: sessionSubType === "DIAGNOSTIC",
		numTotalQuestions: numTotalItems,
		diagnosticLabel,
		studyResourcesMarkdown,
		helpDialogConfig,
		hasListening: hasListeningItems([currentBlock]),
	});

	const prevButtonText = t("session.navigation.previous");
	const allowBackNavigation = getAllowPrevNavigation(responseConfig);
	const showPreviousButton = currentItemIndex > 0 && allowBackNavigation;

	const adjustedShowButtonNavSkip = isIntroScreen(currentItem)
		? false
		: showButtonNavSkip;

	return {
		id,
		currentItemId,
		currentItemIndex,
		numericId,
		collectionId,
		examId,
		examName,
		projectId,
		organizationId,
		sectionId,
		isSummativeTest,
		inReviewMode,
		totalTime,
		sessionSubType,
		sessionItems,
		numSections,
		numTotalItems,
		parts,
		currentPart,
		currentPartIndex,
		numTotalParts,
		sectionIndexWithinPart,
		// current item
		currentItem,
		currentBlock,
		currentItemState,
		isAiSubmittableResponse,
		maximumScore,
		isMarked,
		highlightedHtml,
		slideshowDurationInSeconds,
		// block / progress
		currentBlockIndex,
		blockStates,
		itemStates,
		timeElapsed,
		sessionTime,
		numGoal,
		isLastBlock,
		isLastItemInTheBlock,
		isLastItem,
		// break
		breaks,
		lastBreak,
		isBreakExpired,
		isBreakOngoing,
		showBreakDialog,
		serverExamBreak,
		// UI state
		streaming,
		isMobile,
		isLargeWindowSize,
		sessionItemsNav,
		// bottom bar derived
		showConfidenceSubmitButtons,
		nextButtonText,
		disableNextButton:
			disableNextButton ||
			gateNextUntilResponseComplete ||
			gateNextUntilAudioAnswered ||
			isMicCheckGated,
		showButtonNavSkip: adjustedShowButtonNavSkip,
		nextButtonTooltip,
		isRecordingActive,
		recordingActiveTooltip,
		showButtonNavFinish,
		showSubmitAnswerAIFeedbackButton,
		allowBackNavigation,
		showPreviousButton,
		prevButtonText,
		confirmDialogConfig,
		// actions
		updateProgress,
		updateCurrentBlockIndex,
		toggleShowBreakDialog,
		// confidence onboarding
		hasShownConfidenceOnboardingDialog,
		setHasShownConfidenceOnboardingDialog,
		// full state — needed by helpers until they're refactored
		fullState,
	};
};

export default useSessionBottomBarState;
